import React, { useCallback, useEffect } from 'react';
import { StepProps } from '../components/Step.component';
import { SizingToolState } from '../../models/actionStates';
import ColorSwatch from '../../product_detail/components/ColorSwatch';
import ProductFormButton from '../../product_detail/components/ProductFormButton';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { sizingToolState } from '../../recoil/selectors';
import { allProductsState, pageContextState } from '../../recoil/atoms';
import styles from './ColorPickerViewRender.module.scss';
import { Color } from '../../enums/Color.enum';
import { PageContext } from '../../enums/PageContext.enum';
import { Option } from '../models/option';
import { useSharedGetPreorderState } from '../../hooks/actions/usePreorderState';
import { ViewVariant } from '../../models/ShopifyVariant.model';
import { Preorder } from '../../models/Preorder.model';
import { variantStatus } from '../../util';
import { StepValues } from '../models/step-values';
import { VariantStatus } from '../../enums/VariantStatus.enum';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { uniqueFilter } from '../utils/array-helper';
import { useProductSelection } from '../../hooks/useProductSelection';

interface Props extends StepProps {
  onChange: (option: Option) => () => void;
  getProductPriority?: (stepValues: StepValues) => { preferred: ProductHandle; fallback: ProductHandle } | undefined;
}

export const ColorPickerViewRender: React.FC<Props> = ({ selectionType, selectionKey, getProductPriority, stepValues, onChange }) => {
  const {
    product,
    variant,
    color: activeColor,
    size: activeSize,
    fit: activeFit,
    initializeHandle,
    setColor: setActiveColor,
    subContext,
  } = useProductSelection(selectionType, selectionKey);
  const pageContext = useRecoilValue(pageContextState);
  const setSizingToolState = useSetRecoilState(sizingToolState);

  const allProducts = useRecoilValue(allProductsState);
  const preorders = useSharedGetPreorderState();
  const findPreorder = useCallback(
    (v?: ViewVariant): Preorder => (preorders || []).find((p: Preorder) => p.sku.toLowerCase() === (v?.sku || '').toLowerCase()) || ({} as Preorder),
    [preorders],
  );
  const priority = getProductPriority?.(stepValues);
  const combinedProductColors = priority
    ? [...allProducts[priority.preferred].colors, ...allProducts[priority.fallback].colors].filter(uniqueFilter)
    : product.colors;

  const handleColorChange = (color: Color) => {
    setActiveColor(color);
    if (priority && !product.colors.includes(color)) {
      // If the product we are on doesn't have the desired color, switch to the priority/fallback
      if (priority?.preferred === product.handle) {
        initializeHandle(allProducts[priority.fallback].handle);
      } else {
        initializeHandle(allProducts[priority.preferred].handle);
      }
    }
  };

  useEffect(() => {
    if (activeColor && priority) {
      const preferredVariant = allProducts[priority.preferred].variants.find(
        (v) => v.color === activeColor && v.size === activeSize && (!v.fit || v.fit === activeFit),
      );
      const fallbackVariant = allProducts[priority.fallback].variants.find(
        (v) => v.color === activeColor && v.size === activeSize && (!v.fit || v.fit === activeFit),
      );

      const preferredVariantPreorder = findPreorder(preferredVariant);
      const fallbackVariantPreorder = findPreorder(fallbackVariant);

      const preferredVariantStatus = preferredVariant && variantStatus(preferredVariantPreorder, preferredVariant);
      const fallbackVariantStatus = fallbackVariant && variantStatus(fallbackVariantPreorder, fallbackVariant);

      const setProductToPreferred = () => {
        product.handle !== priority.preferred && initializeHandle(allProducts[priority.preferred].handle);
      };
      const setProductToFallback = () => {
        product.handle !== priority.fallback && initializeHandle(allProducts[priority.fallback].handle);
      };

      if (preferredVariantStatus === VariantStatus.Available) {
        setProductToPreferred();
      } else if (fallbackVariantStatus === VariantStatus.Available) {
        setProductToFallback();
      } else if (!preferredVariant && fallbackVariant) {
        // The preferred variant doesn't exist
        setProductToFallback();
      } else if (preferredVariant && !fallbackVariant) {
        // The fallback variant doesn't exist
        setProductToPreferred();
      } else {
        // TODO: Rethink this code. It currently prioritizes the fallback (v3),
        // as we are using this functionality to prioritize a garment
        // that isn't planning on being restocked (v2)
        // So if the user needs to sign up for a mailing list, we actually
        // show the fallback (v3) so that they will receive email notifications
        // That is why I didn't consolidate the else if above into this else, as
        // we might shift this to prioritize the priority in the future
        setProductToFallback();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeColor]);

  return (
    <div className={styles.hlStColorPicker}>
      <span className={styles.title}>Select your color</span>
      <div className={styles.hlStColorSwatches}>
        {combinedProductColors.map((color) => {
          return <ColorSwatch key={color} color={color} active={variant?.color === color} activate={() => handleColorChange(color)} />;
        })}
      </div>
      <span>Size: {activeSize}</span>
      <div className={styles.hlStFeaturedImage}>
        <img alt="product" src={variant?.featured_image.src} />
      </div>
      <div className={styles.ctaDrawer}>
        <ProductFormButton
          size="large"
          buttonType="secondary"
          beforeSubmit={() => {
            onChange({ nextStepKey: '', text: variant?.color ?? 'Add to bag' })();
            if (pageContext !== PageContext.CartPage) {
              setSizingToolState?.({ sizingToolState: SizingToolState.Closed, selectionType, selectionKey });
            } else {
              setSizingToolState?.({ sizingToolState: SizingToolState.Complete, selectionType, selectionKey });
            }
          }}
          selectionType={selectionType}
          selectionKey={selectionKey}
          subContextOverride={subContext}
        />
      </div>
    </div>
  );
};

export default ColorPickerViewRender;
